import { useCallback, useState } from 'react'

import { AxiosError, AxiosResponse } from 'axios'
import logger from 'utils/logger'

import router from 'components/Router/Router'

const DEFAULT_RESPONSE = {
	loading: false,
	status: undefined,
	success: false,
	error: false,
}

type CallFunction<T, U, R = AxiosResponse<U>> = (...arg: T[]) => Promise<R>
const useApi = <T, U>(
	call: CallFunction<T, U>,
	{ defaultData = null }: { defaultData?: U | null } = {}
) => {
	// we're storing the data as a separate state so that
	// dependencies work properly for other hooks, otherwise
	// we get infinite re-renders for useEffect dependencies sometimes
	const [data, setData] = useState<U | null>(defaultData)
	const [response, setResponse] = useState<{
		error: boolean
		loading: boolean
		status?: number
		success: boolean
	}>(DEFAULT_RESPONSE)

	const resetRequest = useCallback(() => {
		setData(defaultData)
		setResponse((response) => ({
			...response,
			status: undefined,
			success: false,
			error: false,
			loading: false,
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const clearRequest = useCallback(() => {
		setData(null)
		setResponse(DEFAULT_RESPONSE)
	}, [])

	const requestApi = useCallback(
		async (...args: Parameters<CallFunction<T, U>>) => {
			setResponse((response) => ({
				...response,
				status: undefined,
				success: false,
				error: false,
				loading: true,
			}))
			if (call) {
				return await call(...args)
					.then(({ status, data }) => {
						if (status === 200) {
							setResponse((response) => ({
								...response,
								loading: false,
								status,
								success: true,
								error: false,
							}))
							setData(data)
						} else {
							setResponse((response) => ({
								...response,
								loading: false,
								status,
								success: true,
								error: true,
							}))
							setData(data)
						}
					})
					.catch((err: AxiosError) => {
						// Catch bad HTTP responses, like 401
						logger.error(err)
						setResponse((response) => ({
							...response,
							loading: false,
							status: undefined,
							success: false,
							error: true,
						}))
						setData(null)
						if (err.response?.status === 401) {
							// Route to no access page
							router.navigate('/no-access')
						}
					})
					.catch((err: Error) => {
						// Catch all error handler
						logger.error(err)
						setResponse((response) => ({
							...response,
							loading: false,
							status: undefined,
							success: false,
							error: true,
						}))
						setData(null)
					})
			}
		},
		[call]
	)

	return {
		...response,
		clearRequest,
		data,
		requestApi,
		resetRequest,
	}
}

export default useApi
